/**
 * System Settings Service
 * Frontend service for managing global system settings
 * Access: Super admin only
 */

import { auth } from '@/config/firebase';
import type { SystemSettings } from '@simcheck/shared-types';
import { API_URL } from '@/config/env';

// API base URL - same as in api.ts
const API_BASE_URL = API_URL;

/**
 * Get current system settings
 */
export async function getSystemSettings(): Promise<SystemSettings> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }

  const token = await user.getIdToken();
  const response = await fetch(`${API_BASE_URL}/admin/settings`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch settings' }));
    throw new Error(error.message || 'Failed to fetch settings');
  }

  return response.json();
}

/**
 * Update system settings
 */
export async function updateSystemSettings(
  updates: Partial<Omit<SystemSettings, 'id' | 'updatedAt' | 'updatedBy'>>
): Promise<SystemSettings> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }

  const token = await user.getIdToken();
  const response = await fetch(`${API_BASE_URL}/admin/settings`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to update settings' }));
    throw new Error(error.message || 'Failed to update settings');
  }

  return response.json();
}

/**
 * Default settings values (used when settings haven't been initialized)
 */
export const DEFAULT_SETTINGS: Omit<SystemSettings, 'id' | 'updatedAt' | 'updatedBy'> = {
  networkStalenessHours: 4,
  networkScanIntervalMinutes: 10,
  maxConcurrentExecutions: 5,
  defaultExecutionTimeoutMinutes: 15,
  lockTtlMinutes: 15,
  lockCleanupIntervalMinutes: 5,
  requireApprovalForTrial: true,
  connectivityModesEnabled: true,
};
